<template>
  <div class="gps">
    <Breadcrumbs v-bind:breadcrumbs="breadcrumbs" />

    <DataTable
      class="p-datatable-sm p-datatable-striped p-datatable-gridlines"
      :value="getGps"
      :loading="isLoading"
      :rowHover="true"
      :lazy="true"
      filterDisplay="row"
      :stateKey="tableStateStore.filterName()"
      :filters.sync="tableStateStore.filters"
      :rows.sync="tableStateStore.pagination.pageSize"
      :sortField="tableStateStore.sorting.sortField"
      :sortOrder="tableStateStore.sorting.sortOrder"
      :first="tableStateStore.skip()"
      stateStorage="local"
      :totalRecords="getGpsCount"
      :paginator="true"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="
        $t('show') +
          ' {first} ' +
          $t('to') +
          ' {last} ' +
          $t('of') +
          ' {totalRecords} ' +
          $t('records')
      "
      :rowsPerPageOptions="[10, 20, 50, 100]"
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
      @state-restore="onStateRestore($event)"
      responsiveLayout="scroll"
    >
      <template #header>
        <div class="table-header">
          <div class="table-header-left">
            <h1 class="page-title">{{ $t('gps_data') }}</h1>
          </div>
          <div class="table-header-right">
            <button class="btn btn-narrow btn-inverse ml-2 mb-2" @click.prevent="onRemoveFilters()">
              <i class="fa fa-remove mr-2"></i>
              {{ $t('remove_filters') }}
            </button>
          </div>
        </div>
      </template>
      <template #empty>
        {{ $t('no_data_found') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>

      <Column
        field="number"
        header="#"
        sortable
        :headerStyle="{ width: '80px' }"
        :bodyStyle="'text-align: center;width: 80px'"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.number !== null">
            {{ slotProps.data.number.toString().padStart(4, '0') }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column field="user" :header="$t('users')" headerClass="center" className="center" sortable>
        <template #body="slotProps">
          {{ slotProps.data.user.firstname }} {{ slotProps.data.user.lastname }} ({{
            slotProps.data.user.email
          }})
        </template>

        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="createdAt"
        :header="$t('date')"
        headerClass="center"
        className="center"
        sortable
        filterField="dateRange"
      >
        <template #body="slotProps">
          {{ $datetime(slotProps.data.createdAt) }}
        </template>

        <template #filter="{filterModel,filterCallback}">
          <Calendar
            ref="gpsDateRangeCalendar"
            @input="filterCallback()"
            v-model="filterModel.range"
            selectionMode="range"
            class="p-column-filter"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date')"
            :showWeek="true"
            :yearNavigator="true"
            :monthNavigator="true"
          >
          </Calendar>
        </template>
      </Column>

      <Column field="long" :header="$t('longitude')" sortable filterMatchMode="contains">
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.long !== null">
            {{ formatCoordinate(slotProps.data.long) }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column field="lat" :header="$t('latitude')" sortable filterMatchMode="contains">
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.lat !== null">
            {{ formatCoordinate(slotProps.data.lat) }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column :header="$t('maps')" headerClass="center" className="center">
        <template #body="slotProps">
          <a
            v-if="slotProps.data && slotProps.data.lat !== null && slotProps.data.long !== null"
            :href="generateGoogleMapsLinkFromCoordinates(slotProps.data.lat, slotProps.data.long)"
            target="_blank"
            class="btn btn-sm btn-primary"
          >
            <i class="fa fa-map-marker mr-1"></i>
            {{ $t('view_on_map') }}
          </a>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Breadcrumbs from '@/components/Breadcrumbs';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import dayjs from 'dayjs';
import Calendar from 'primevue/calendar';
import { generateGoogleMapsLinkFromCoordinates } from '@/helpers/integration';

export default {
  name: 'GpsPage',
  components: {
    Breadcrumbs,
    DataTable,
    InputText,
    Column,
    Calendar,
  },
  data() {
    return {
      gpsTableStateConfig: {
        name: 'gps',
        filters: {
          number: { value: null },
          user: { value: null },
          dateRange: {
            range: [
              this.$dayjs()
                .subtract(14, 'days')
                .toDate(),
            ],
          },
          long: { value: null },
          lat: { value: null },
        },
        customFilters: {},
        sorting: { sortField: 'timeStamp', sortOrder: -1 }, // Default sort by timestamp descending
      },
    };
  },
  computed: {
    ...mapGetters(['getGps', 'getGpsCount', 'isLoading', 'getTableState']),
    breadcrumbs() {
      return [{ name: 'Home', route: { name: 'home' } }, { name: this.$t('gps_data') }];
    },
    tableStateStore() {
      return this.getTableState(this.gpsTableStateConfig.name);
    },
  },
  methods: {
    ...mapActions(['fetchGps', 'initTableState']),
    generateGoogleMapsLinkFromCoordinates,
    /**
     * Format timestamp to dd.mm.yyyy hh:mm:ss
     */
    formatTimestamp(timestamp) {
      return dayjs(timestamp).format('DD.MM.YYYY HH:mm:ss');
    },

    /**
     * Format coordinate to show reasonable precision
     */
    formatCoordinate(coordinate) {
      return coordinate.toFixed(6);
    },

    /**
     * Load remote table data
     */
    async loadTableData() {
      await this.fetchGps({
        pagination: {
          skip: this.tableStateStore.skip(),
          pageSize: this.tableStateStore.pagination.pageSize,
        },
        sorting: this.tableStateStore.sorting,
        filters: { ...this.tableStateStore.filters, ...this.tableStateStore.customFilters },
      });
    },

    /**
     * Load more results from server or cache on pagination click
     */
    async onPage(event) {
      this.tableStateStore.pagination = { page: event.page, pageSize: event.rows };
      this.loadTableData();
    },

    /**
     * Load results from server/cache on sort
     */
    async onSort(event) {
      this.tableStateStore.sorting = { sortField: event.sortField, sortOrder: event.sortOrder };
      this.loadTableData();
    },

    /**
     * Load results from server/cache on filter
     */
    async onFilter(event) {
      // force dateRange end date to end of day
      if (
        this.tableStateStore.filters.dateRange.range &&
        this.tableStateStore.filters.dateRange.range[1]
      ) {
        // Set end date to the end of the day
        this.tableStateStore.filters.dateRange.range[1] = this.$dayjs(
          this.tableStateStore.filters.dateRange.range[1]
        )
          .endOf('day')
          .toDate();
      }

      this.loadTableData();
    },

    /**
     * Remove all filters
     * @param {*} event
     */
    async onRemoveFilters(event) {
      this.tableStateStore.reset();
      await this.loadTableData();
    },

    /**
     * Trigger when state is loaded from local storage
     */
    async onStateRestore(event) {
      this.tableStateStore.restore(event);
    },
  },
  async created() {
    await this.initTableState(this.gpsTableStateConfig);
  },
  async mounted() {
    await this.loadTableData();
  },
};
</script>

<style scoped lang="scss">
::v-deep .p-column-filter-clear-button {
  display: none;
  width: 0;
}
::v-deep .p-column-filter-menu-button {
  display: none;
  width: 0;
}
/** Fix Dropdown overlay */
::v-deep .p-datatable-responsive-scroll > .p-datatable-wrapper {
  min-height: 75vh;
}
</style>
